import { ChangeDetectionStrategy, Component, computed, input, model, output, signal, inject, ViewChild, linkedSignal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DatePickerSelection, DateSelectionType } from './date-picker.interfaces';
import { IonModal } from '@ionic/angular/standalone';
import { IconComponent } from '../ui/icon/icon.component';
import { ButtonComponent } from '../ui/button/button';
import { ToastService } from 'libs/core/toast/toast.service';
import { differenceInDays, isBefore } from 'date-fns';

@Component({
  selector: 'lib-modal-date-picker',
  standalone: true,
  imports: [CommonModule, IonModal, IconComponent, ButtonComponent],
  templateUrl: './modal-date-picker.html',
  styleUrl: './modal-date-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalDatePicker {
  @ViewChild(IonModal) modal!: IonModal;
  private toastService = inject(ToastService);

  /**
   * Two-way binding for the date selection.
   */
  selection = input<DatePickerSelection>({
    startDate: null,
    endDate: null,
    type: 'none'
  });

  // Rang que va escollint l'usuari
  draftSelection = linkedSignal<DatePickerSelection>(() => {
    const s = this.selection();
    let daysDifference;
    if(this.selection().endDate != null && this.selection().startDate != null){
      daysDifference = differenceInDays(this.selection().endDate!, this.selection().startDate!)
    } 
    let endDateIsToday = this.selection().endDate !== null ? this.isToday(this.selection().endDate!) : false
    
    let type: DateSelectionType = "custom";
    if(daysDifference === 0 && endDateIsToday) type = "today";
    else if (daysDifference === 6 && endDateIsToday) type = "week";
    else if (this.selection().startDate !== null && this.selection().startDate?.getDate() === 1 && endDateIsToday) type = "lastMonth";
    else if (this.selection().startDate !== null && this.selection().endDate !== null) type = "custom";
    else type = "none";
    return {
      startDate: s.startDate ? new Date(s.startDate) : null,
      endDate: s.endDate ? new Date(s.endDate) : null,
      type: type
    }
  });

  /**
   * Whether the modal is open.
   */
  isOpen = model<boolean>(false);

  /**
   * Emitted when the selection is confirmed.
   */
  confirmed = output<DatePickerSelection>();

  /**
   * Emitted when the modal is closed without confirmation.
   */
  closed = output<void>();

  /**
   * !Maxim de dies seleccionables (per defecte 92)
   */
  maxDays = input<number>(92);

  // Estat del calendari
  // Utilitzem linkedSignal perquè el mes visible s'actualitzi al canviar-lo desde fora (via selector-date-picker)
  viewDate = linkedSignal(() => {
    const startDate = this.draftSelection().startDate;
    const endDate = this.draftSelection().endDate;
    const anchor = startDate ?? endDate ?? new Date();
    return new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  });
  
  monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  dayNames = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];

  constructor() {
    effect(() => {
    });
  }

  // !Genera els dies per a la vista actual (35 dies)
  calendarDays = computed(() => {
    const date = this.viewDate();
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // Volem que dilluns sigui 0
    let firstDayOfMonth = new Date(year, month, 1).getDay();
    firstDayOfMonth = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days: (Date | null)[] = [];
    
    //@ Afegeix dies del mes anterior per completar la primera fila
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      days.push(new Date(year, month - 1, prevMonthLastDay - i));
    }
    
    // Dies mes actual
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    //@ Afegeix dies del mes següent fins a completar la 5 fila
    const totalSlots = 42;
    const remainingDays = totalSlots - days.length;
    
    if (remainingDays > 0) {
      for (let i = 1; i <= remainingDays; i++) {
        days.push(new Date(year, month + 1, i));
      }
    } else if (remainingDays < 0) {
      return days.slice(0, totalSlots);
    }
    
    return days;
  });

  // Selecció de rangs preestablerts
  selectType(type: DateSelectionType) {
    this.draftSelection.update(current => ({ ...current, type }));
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let pastDate = new Date(today);

    if (type === 'week') {
      pastDate.setDate(pastDate.getDate() - 6);
    } else if (type === 'lastMonth') {
      pastDate.setDate(1);
    }
    
    //? Calculem diferencia de dies
    const diffTime = Math.abs(pastDate.getTime() - today.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays >= this.maxDays()) {
      //! Si supera el limit borrem la selecció i mostrem toast
      this.setRange(null, null);
      this.draftSelection.update(prev => ({ ...prev, type: 'none' }));
      
      this.toastService.presentToast('warning', 'Límite excedido', `La selección no puede superar los ${this.maxDays()} días.`, 'bottom', 5000);
      return;
    }

    this.setRange(pastDate, today);
    this.viewDate.set(new Date(today));
  }

  setRange(startDate: Date | null, endDate: Date | null) {
    if (startDate) startDate.setHours(0,0,0,0);
    if (endDate) endDate.setHours(0,0,0,0);
    
    this.draftSelection.update(current => ({
      ...current,
      startDate,
      endDate
    }));
  }

  // Navegació de mesos
  prevMonth() {
    const current = this.viewDate();
    this.viewDate.set(new Date(current.getFullYear(), current.getMonth() - 1, 1));
  }

  nextMonth() {
    const current = this.viewDate();
    this.viewDate.set(new Date(current.getFullYear(), current.getMonth() + 1, 1));
  }

  // Selecció manual de dates al calendari
  toggleDate(date: Date) {
    const d = new Date(date);
    d.setHours(0,0,0,0);
    
    const current = this.draftSelection();
    this.draftSelection.update(prev => ({ ...prev, type: 'custom' }));
    
    if (!current.startDate || (current.startDate && current.endDate) || isBefore(date, current.startDate)) {
      this.setRange(d, null);
    } else {
      const start = new Date(current.startDate);
      start.setHours(0,0,0,0);
      
      //? Calculem diferencia de dies
      const diffTime = Math.abs(d.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays >= this.maxDays()) {
        this.toastService.presentToast('warning', 'Límite excedido', `La selección no puede superar los ${this.maxDays()} días.`, 'bottom', 5000);
        return;
      }

      if (d < start) {
        this.setRange(d, start);
      } else {
        this.setRange(start, d);
      }
    }
  }

  isDateSelected(date: Date): boolean {
    const start = this.draftSelection().startDate;
    const end = this.draftSelection().endDate;
    if (!start) return false;
    
    const d = new Date(date);
    d.setHours(0,0,0,0);
    
    const s = new Date(start);
    s.setHours(0,0,0,0);
    
    if (!end) return d.getTime() === s.getTime();
    
    const e = new Date(end);
    e.setHours(0,0,0,0);
    
    return d.getTime() >= s.getTime() && d.getTime() <= e.getTime();
  }

  isRangeStart(date: Date): boolean {
    const start = this.draftSelection().startDate;
    if (!start) return false;
    const d = new Date(date);
    d.setHours(0,0,0,0);
    const s = new Date(start);
    s.setHours(0,0,0,0);
    return d.getTime() === s.getTime();
  }

  isRangeEnd(date: Date): boolean {
    const end = this.draftSelection().endDate;
    if (!end) return false;
    const d = new Date(date);
    d.setHours(0,0,0,0);
    const e = new Date(end);
    e.setHours(0,0,0,0);
    return d.getTime() === e.getTime();
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() && 
           date.getMonth() === today.getMonth() && 
           date.getFullYear() === today.getFullYear();
  }

  isCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.viewDate().getMonth();
  }

  confirm() {
    // if(this.draftSelection().endDate == null) this.draftSelection.update(d => {return {...d, endDate: d.startDate}})
    const d = this.draftSelection();

    const fixed: DatePickerSelection = {
      ...d,
      endDate: d.endDate ?? d.startDate,
    };
    this.confirmed.emit(fixed);
    this.isOpen.set(false);
  }
  
  closeModal() {
    this.isOpen.set(false);
    this.closed.emit();
  }
}
